import { createSelector, defaultMemoize } from 'reselect'
import { isNull, get, filter, isUndefined } from 'lodash'
import {
  parseQsArg,
  checkUserCanSwap,
  getProponenteItems,
  getDestinatarioItems,
  getProponenteCash,
  getDestinatarioCash,
  getProponenteEsito,
  getDestinatarioEsito,
  isInteger,
  isExternalItemId,
} from '../utils'
import { defaultTopBarConfigState } from '../reducers/ui'

// *******************************************************
// TODO: Move in /utils.js
// *******************************************************
const maybeNull = a => fn => (isNull(a) ? null : fn(a))

// *******************************************************
// TODO: auth.js
// *******************************************************
const getUser = state => state.auth.user

export const getUserCanSwap = createSelector(getUser, checkUserCanSwap)

// *******************************************************
// TODO: topbar.js
// *******************************************************
export const getTopBarConfig = createSelector(
  state => state.ui.topBarConfig,
  configs => {
    const maxIndex = Math.max(...Object.keys(configs).map(k => +k))
    return isFinite(maxIndex) ? configs[maxIndex] : defaultTopBarConfigState
  }
)

// *******************************************************
// TODO: categoriesAndWorlds.js
// *******************************************************
const getCategoriesSlugs = state => state.categoriesAndWorlds.categoriesSlugs
const getCategoriesBySlug = state => state.categoriesAndWorlds.categoriesBySlug

const getWorldsSlugs = state => state.categoriesAndWorlds.worldsSlugs
const getWorldsBySlug = state => state.categoriesAndWorlds.worldsBySlug

const getCategoriesSlugsByWorld = state =>
  state.categoriesAndWorlds.categoriesByWorld

export const makeGetCategoriesByWorld = defaultMemoize(worldSlug =>
  createSelector(
    getCategoriesSlugsByWorld,
    getCategoriesBySlug,
    (categoriesSlugsByWorld, categoriesBySlug) =>
      (categoriesSlugsByWorld[worldSlug] || []).map(
        catSlug => categoriesBySlug[catSlug]
      )
  )
)

export const getCategories = createSelector(
  getCategoriesSlugs,
  getCategoriesBySlug,
  (slugs, bySlug) => slugs.map(slug => bySlug[slug])
)

export const getWorlds = createSelector(
  getWorldsSlugs,
  getWorldsBySlug,
  (slugs, bySlug) => slugs.map(slug => bySlug[slug])
)

export const getWorld = (state, worldSlug) =>
  state.categoriesAndWorlds.worldsBySlug[worldSlug]

export const getCategory = (state, categorySlug) =>
  state.categoriesAndWorlds.categoriesBySlug[categorySlug]

export const getWorldsWithCategories = createSelector(
  getWorlds,
  getCategoriesSlugsByWorld,
  getCategoriesBySlug,
  (worlds, categoriesByWorlds, categoriesBySlug) =>
    worlds.map(world => ({
      ...world,
      categories: categoriesByWorlds[world.slug].map(
        slug => categoriesBySlug[slug]
      ),
    }))
)

// *******************************************************
// TODO: districts.js
// *******************************************************
export const getDistricts = createSelector(
  state => state.districts.codeList,
  state => state.districts.byCode,
  (codeList, byCode) => codeList.sort().map(code => byCode[code])
)

// Not really a selector util function grab district object from location :P
export const getDistrictFilter = (state, location, key = 'id') => {
  const districtCode = parseQsArg(location, 'district').toUpperCase()
  if (
    !districtCode ||
    typeof state.districts.byCode[districtCode] === 'undefined'
  ) {
    return ''
  }
  const districtObject = state.districts.byCode[districtCode]
  return key ? get(districtObject, key) : districtObject
}

// *******************************************************
// TODO: helpers.js
// *******************************************************
const makePaginationSelectors = selectItems => {
  const getIds = state => selectItems(state).ids
  const getData = state => selectItems(state).data
  const getLoading = state => selectItems(state).loading
  const getPagination = state => selectItems(state).pagination
  const getTotal = state => getPagination(state).total

  const makeList = (ids, data) => maybeNull(ids)(ids => ids.map(id => data[id]))
  const checkCanLoadMore = pagination => pagination.nextOffset !== null

  const getAllList = createSelector(getIds, getData, makeList)

  const canLoadMore = createSelector(getPagination, checkCanLoadMore)

  return [getAllList, canLoadMore, getTotal, getLoading]
}

// *******************************************************
// TODO: items.js
// *******************************************************
const makeItemsSelectors = (selectItems, funcs = []) => {
  const getIds = state => selectItems(state).ids
  const getData = state => selectItems(state).data
  const getLoading = state => selectItems(state).loading
  const getPagination = state => selectItems(state).pagination
  const getTotal = state => getPagination(state).total
  const getCache = state => selectItems(state).cache

  const makeItem = (data, user) => _id => {
    // External item
    if (isExternalItemId(_id)) {
      return {
        ...data[_id],
        posseduto_da: [],
        _id,
      }
    }

    // No matter owner
    if (isInteger(_id)) {
      return {
        ...data[_id],
        _id,
      }
    }
    const [id, ownerKey] = _id.split('-')
    const item = data[id]

    // Item \w no owner
    if (ownerKey === '') {
      return {
        ...item,
        _id,
        posseduto_da: [],
      }
    }

    // Item \w one or more owners
    const ownersIds = ownerKey.split(',')
    const byOwner = item.posseduto_da.byOwner
    const owners = filter(ownersIds.map(ownerId => byOwner[ownerId]))

    // mmm Ok this means no more consistent _id so we
    // mark this as invalid item
    if (owners.length !== ownersIds.length) {
      return null
    }

    // Super edge case! When posseduto is not provided
    // (when update from reducer Es:. item updated)
    // calculate using current auth user...
    const posseduto = isUndefined(item.posseduto)
      ? user ? !isUndefined(byOwner[user.id]) : false
      : item.posseduto

    return {
      ...item,
      _id,
      posseduto,
      posseduto_da: owners,
    }
  }
  const makeList = (ids, data, user) =>
    maybeNull(ids)(ids => filter(ids.map(makeItem(data, user))))

  const getBaseAllList = createSelector(
    getIds,
    getData,
    state => state.auth.user,
    makeList
  )
  const getAllList = funcs.length
    ? createSelector(...[getBaseAllList, ...funcs])
    : getBaseAllList

  const checkCanLoadMore = pagination => pagination.nextOffset !== null
  const canLoadMore = createSelector(getPagination, checkCanLoadMore)

  const isItemsCacheValid = (state, cacheKey) => {
    const prevCacheKey = getCache(state).key
    // No cache
    if (!prevCacheKey) {
      return true
    }
    return prevCacheKey === cacheKey
  }

  const getAllListWithCache = (state, cacheKey) => {
    if (isUndefined(cacheKey) || isItemsCacheValid(state, cacheKey)) {
      return getAllList(state)
    }
    return null
  }

  const getScrollToIndex = (state, cacheKey) => {
    if (isItemsCacheValid(state, cacheKey)) {
      return getCache(state).index
    }
    return null
  }

  return [
    getAllListWithCache,
    canLoadMore,
    getTotal,
    getLoading,
    getScrollToIndex,
  ]
}

export const [
  getItems,
  canLoadMoreItems,
  getTotalItems,
  getItemsLoading,
] = makeItemsSelectors(state => state.allItems)

export const [
  getSearchItems,
  canLoadMoreSearchItems,
  getTotalSearchItems,
  getSearchItemsLoading,
] = makeItemsSelectors(state => state.searchItems)

export const [
  getOwnedItems,
  canLoadMoreOwnedItems,
  getTotalOwnedItems,
  getOwnedItemsLoading,
  getOwnedItemsScrollToIndex,
] = makeItemsSelectors(state => state.ownedItems)

export const [
  getWantedItems,
  canLoadMoreWantedItems,
  getTotalWantedItems,
  getWantedItemsLoading,
  getWantedItemsScrollToIndex,
] = makeItemsSelectors(state => state.wantedItems)

export const [
  getInterestItems,
  canLoadMoreInterestItems,
  getTotalInterestItems,
  getInterestItemsLoading,
] = makeItemsSelectors(state => state.interestItems, [
  state => state.interestItems.interestsRemovingById,
  (items, removingById) =>
    maybeNull(items)(items => items.filter(item => !removingById[item.id])),
])

export const [
  getItemsICanGet,
  canLoadMoreItemsICanGet,
  getTotalItemsICanGet,
  getItemsICanGetLoading,
] = makeItemsSelectors(state => state.itemsICanGet)

export const [
  getWantedItemsOfOtherUser,
  canLoadMoreWantedItemsOfOtherUser,
  getTotalWantedItemsOfOtherUser,
  getWantedItemsOfOtherUserLoading,
  getWantedItemsOfOtherUserScrollToIndex,
] = makeItemsSelectors(state => state.otherUser.wantedItems)

export const [
  getOwnedItemsOfOtherUser,
  canLoadMoreOwnedItemsOfOtherUser,
  getTotalOwnedItemsOfOtherUser,
  getOwnedItemsOfOtherUserLoading,
  getOwnedItemsOfOtherUserScrollToIndex,
] = makeItemsSelectors(state => state.otherUser.ownedItems)

export const [
  getItemsICanGetFromOtherUser,
  canLoadMoreItemsICanGetFromOtherUser,
  getTotalItemsICanGetFromOtherUser,
  getItemsICanGetFromOtherUserLoading,
  getItemsICanGetFromOtherUserScrollToIndex,
] = makeItemsSelectors(state => state.otherUser.itemsICanGet)

export const [
  getItemsICanGiveToOtherUser,
  canLoadMoreItemsICanGiveToOtherUser,
  getTotalItemsICanGiveToOtherUser,
  getItemsICanGiveToOtherUserLoading,
] = makeItemsSelectors(state => state.otherUser.itemsICanGive)

export const [
  getMessages,
  canLoadMoreMessages,
  getTotalMessages,
  getMessagesLoading,
] = makePaginationSelectors(state => state.messages)

// *******************************************************
// TODO: messages.js
// *******************************************************
export const getInboxMessages = createSelector(
  getMessages,
  getUser,
  (messages, user) =>
    maybeNull(messages)(m =>
      m.filter(message => message.mittente.id !== user.id)
    )
)

export const getOutboxMessages = createSelector(
  getMessages,
  getUser,
  (messages, user) =>
    maybeNull(messages)(m =>
      m.filter(message => message.mittente.id === user.id)
    )
)

// *******************************************************
// TODO: fb.js
// *******************************************************
export const getFbData = state => state.fb.data
export const getFbLoading = state => state.fb.loading
export const getFbError = state => state.fb.error

// *******************************************************
// TODO: swaps.js
// *******************************************************
const [
  getSwapsUnormalized,
  canLoadMoreSwaps,
  getTotalSwaps,
  getSwapsLoading,
] = makePaginationSelectors(state => state.swaps)

const normalizeSwap = swap => ({
  ...swap,
  proponenteItems: getProponenteItems(swap),
  destinatarioItems: getDestinatarioItems(swap),
  proponenteMoney: getProponenteCash(swap),
  destinatarioMoney: getDestinatarioCash(swap),
  proponenteEsito: getProponenteEsito(swap),
  destinatarioEsito: getDestinatarioEsito(swap),
})

const getSwaps = createSelector(getSwapsUnormalized, swaps =>
  maybeNull(swaps)(swaps => swaps.map(normalizeSwap))
)

export { getSwaps, canLoadMoreSwaps, getTotalSwaps, getSwapsLoading }

const denormalizeSwap = swap => ({
  ...swap,
  proponenteItems: swap.proponenteItems.ids.map(
    id => swap.proponenteItems.data[id]
  ),
  proponenteItemsIds: swap.proponenteItems.ids,
  proponenteItemsData: swap.proponenteItems.data,
  destinatarioItems: swap.destinatarioItems.ids.map(
    id => swap.destinatarioItems.data[id]
  ),
  destinatarioItemsIds: swap.destinatarioItems.ids,
  destinatarioItemsData: swap.destinatarioItems.data,
})

export const getNewSwap = createSelector(
  state => state.newSwap.swap,
  swap => maybeNull(swap)(denormalizeSwap)
)

export const getSwapDetail = createSelector(
  state => state.swapDetail.swap,
  swap => maybeNull(swap)(denormalizeSwap)
)

export const getIsSwapReversed = createSelector(
  state => state.swapDetail.swap,
  state => state.swapDetail.originalSwap,
  (swap, originalSwap) =>
    maybeNull(swap)(() => {
      return swap.proponente.id === originalSwap.destinatario.id
    })
)

// Genere values

export const getGenereValuesByCategory = defaultMemoize(categoryId =>
  createSelector(
    state => state.genereValues,
    allValues => {
      if (isUndefined(allValues[categoryId])) {
        return []
      }
      return allValues[categoryId].values.map(({ genere, count }) => ({
        label: genere,
        count,
      }))
    }
  )
)
export const getGenereValuesFlatByCategory = defaultMemoize(categoryId =>
  createSelector(
    state => state.genereValues,
    allValues => {
      if (isUndefined(allValues[categoryId])) {
        return []
      }
      return allValues[categoryId].values.map(({ genere }) => genere)
    }
  )
)

export const getGenereIsLoadingByCategory = defaultMemoize(categoryId =>
  createSelector(
    state => state.genereValues,
    allValues => {
      // Not alredy started ... no loading
      if (isUndefined(allValues[categoryId])) {
        return false
      }
      return allValues[categoryId].loading
    }
  )
)

// Attribute values

export const getAttributeValues = defaultMemoize(attribute =>
  createSelector(
    state => state.attributeValues,
    allValues => {
      if (isUndefined(allValues[attribute])) {
        return []
      }
      return allValues[attribute].values
        .map(({ valore, count }) => ({
          label: valore,
          count,
        }))
        .slice(0, 10)
    }
  )
)



// WEBPACK FOOTER //
// ./src/selectors/index.js